import { Document, View } from '@react-pdf/renderer';
import { getCreditUsed, getDiscountValue, getTaxValue, getTotalValue } from '../../../pricing';
import { en } from '../../theme/languages/en';
import { BillingAccountDetails, Invoice, PricingModelTab, Supplier } from '../../../common';
import {
  CreditNoteContent,
  EntityBankData,
  InvoiceMetaData,
  PageComponent,
  Span,
  SummaryData,
  SummaryProductTable,
  SummaryTable,
  UsageLogs,
  CryptoPaymentData,
} from '../../components';
import { getBankData, getPaymentLink, isDocumentHasCreditCardPayment, sum } from '../../utils/general';
import { getInvoiceIdName, invoiceSummaryValuesList } from '../../utils/invoiceSummary';
import { isUsageLogsExist } from '../../utils/usageUtils';
import { styles } from './InvoicePDF.module';

interface InvoicePDFProps {
  invoiceData: Invoice;
  supplier?: Supplier;
  bankDetails?: BillingAccountDetails;
  isPreview?: boolean;
}

export const InvoicePDF = ({ invoiceData, supplier, bankDetails, isPreview }: InvoicePDFProps) => {
  const discountValue = sum(
    invoiceData?.pricingModel?.tabs.map((tab: PricingModelTab) => getDiscountValue(tab?.subTotal, tab?.discount)),
  );
  const taxValue = sum(
    invoiceData?.pricingModel?.tabs.map((tab: PricingModelTab) => getTaxValue(tab?.subTotal, tab?.discount, tab?.tax)),
  );
  const totalValue = sum(
    invoiceData?.pricingModel?.tabs.map((tab: PricingModelTab) =>
      getTotalValue(tab?.subTotal, tab?.discount, tab?.tax),
    ),
  );

  const subTotalValue = sum(invoiceData?.pricingModel?.tabs.map((tab: PricingModelTab) => tab?.subTotal));

  return (
    <Document>
      <PageComponent
        supplier={supplier || invoiceData.supplier}
        isPreview={isPreview}
        hasCreditCardPayment={isDocumentHasCreditCardPayment(invoiceData)}
        paymentLink={getPaymentLink(invoiceData)}
        documentState={invoiceData?.state}
      >
        <View style={styles.container}>
          <InvoiceMetaData
            title={`${en.invoice} ${getInvoiceIdName(invoiceData)}`}
            invoiceData={invoiceData}
            showBillingPeriod={invoiceData?.pricingModel?.tabs.length == 1}
          />

          {invoiceData?.pricingModel?.tabs.length > 1 ? (
            <SummaryTable products={invoiceData?.pricingModel?.tabs} periodColumnName={en.billingPeriod} />
          ) : (
            <SummaryProductTable
              product={invoiceData?.pricingModel?.tabs[0]}
              isPartialPeriod={invoiceData?.pricingModel?.tabs[0]?.isPartialPeriod}
              style={styles.productTable}
              showOnlyTable
            />
          )}

          <SummaryData
            summaryItems={invoiceSummaryValuesList({
              currency: invoiceData?.pricingModel?.tabs[0].currency,

              subTotalCalculatedValue: subTotalValue,
              discountCalculatedValue: discountValue,
              taxCalculatedValue: taxValue,

              hidePercentage: invoiceData?.pricingModel?.tabs.length > 1,

              taxPercentage:
                invoiceData?.pricingModel?.tabs.length == 1 ? invoiceData?.pricingModel?.tabs[0].tax : undefined,
              discount:
                invoiceData?.pricingModel?.tabs.length == 1 ? invoiceData?.pricingModel?.tabs[0].discount : undefined,

              isCreditsExist: invoiceData?.invoiceCredits?.length > 0,
              creditEstimations: invoiceData?.creditsAmount,
              total: totalValue,
              totalDue:
                invoiceData?.invoiceCredits?.length || invoiceData?.creditsAmount
                  ? getCreditUsed(totalValue, invoiceData?.creditsAmount)
                  : totalValue,
              credits: invoiceData?.creditsAmount,
              isPartialPeriod: invoiceData?.pricingModel?.tabs.some((tab) => tab.isPartialPeriod),
            })}
          />

          {invoiceData?.pricingModel?.tabs?.length == 1 && (
            <View style={styles.noteContainer} wrap={false}>
              <Span style={styles.note} canBeMissingValue missingValue={invoiceData?.pricingModel?.tabs[0]?.note}>
                {en.note}
              </Span>
              <Span style={styles.text} canBeMissingValue missingValue={invoiceData?.pricingModel?.tabs[0]?.note}>
                {invoiceData?.pricingModel?.tabs[0]?.note}
              </Span>
            </View>
          )}

          <View style={styles.bottomContainer}>
            {!!bankDetails?.bank && <EntityBankData title={en.paymentDetails} data={getBankData(bankDetails)} />}

            {invoiceData?.cryptoSettings?.isEnabled && <CryptoPaymentData data={invoiceData?.cryptoSettings} />}
          </View>
        </View>
      </PageComponent>

      {invoiceData?.pricingModel?.tabs.length > 1 && (
        <PageComponent
          supplier={supplier || invoiceData.supplier}
          isPreview={isPreview}
          documentState={invoiceData?.state}
        >
          <View style={styles.productTablesContainer}>
            {invoiceData?.pricingModel.tabs.map((tab, index) => (
              <SummaryProductTable key={index} product={tab} isPartialPeriod={tab?.isPartialPeriod} />
            ))}
          </View>
        </PageComponent>
      )}

      {isUsageLogsExist(invoiceData?.usageReportsByProduct) && invoiceData.customer?.settings?.attachUsageLogsPdf && (
        <UsageLogs
          usageReportsByProduct={invoiceData?.usageReportsByProduct}
          poNumber={invoiceData?.poNumber}
          isPreview={isPreview}
          supplier={supplier}
        />
      )}

      {invoiceData?.invoiceCredits?.length > 0 &&
        invoiceData?.invoiceCredits?.map((invoiceCredit) => (
          <PageComponent
            supplier={supplier || invoiceData.supplier}
            isPreview={isPreview}
            documentState={invoiceData?.state}
          >
            <CreditNoteContent
              creditNoteData={invoiceCredit.creditNote}
              invoiceCredit={invoiceCredit}
              supplier={supplier}
            />
          </PageComponent>
        ))}
    </Document>
  );
};
